import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  jobPostingsApi,
  jobCategoriesApi,
  jobTagsApi,
  jobPostingFeaturedImageUploadApi,
  jobPostingsUserList,
} from "proxyUrl";
import Select from "react-select";

// File Upload Component for Featured Image
const FileUpload = ({
  endpoint,
  fieldName,
  onUploadComplete,
  accept,
  label,
  currentUrl,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append(fieldName, file);

    try {
      const response = await axios.post(endpoint, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data) {
        // Handle different response structures
        const data = response.data.data || response.data;
        const url = data.featuredImageUrl || data.featuredImage || data.imageUrl || data.url || response.data.featuredImageUrl || response.data.imageUrl || response.data.url;
        if (url) {
          onUploadComplete(url);
          toast.success("File uploaded successfully");
        } else {
          throw new Error("No URL returned from server");
        }
      }
    } catch (err) {
      console.error("File upload error", err);
      setError(err.response?.data?.message || "Upload failed");
      toast.error("File upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="file"
          accept={accept}
          onChange={handleFileChange}
          disabled={uploading}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue disabled:opacity-50"
        />
        {uploading && (
          <span className="text-xs text-gray-500">Uploading...</span>
        )}
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {currentUrl && (
        <div className="space-y-1">
          <a
            href={currentUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-indigo-600 hover:underline"
          >
            View current image
          </a>
          <div className="mt-2">
            <img
              src={currentUrl}
              alt="Featured"
              className="h-20 w-20 rounded-lg object-cover border border-gray-200"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const steps = [
  "Job information",
  "Application details",
  "Job pipeline",
  "Automated emails",
  "Review",
];

const defaultApplicationQuestions = {
  resume: { enabled: false },
  linkedin: { enabled: false },
  github: { enabled: false },
  portfolio: { enabled: false },
  coverLetter: { enabled: false },
  workEligibility: { enabled: false },
  relocation: { enabled: false },
  noticePeriod: { enabled: false },
  currentCtc: { enabled: false },
  expectedCtc: { enabled: false },
  references: { enabled: false },
};

const defaultPipeline = [
  { name: "New", color: "#16a34a" },
  { name: "Phone Screened", color: "#0891b2" },
  { name: "Interview", color: "#f97316" },
  { name: "Offer", color: "#a855f7" },
  { name: "Hired", color: "#10b981" },
];

const defaultEmails = [
  {
    key: "scheduleInterview",
    label: "Schedule Interview",
    enabled: false,
    subject: "",
    body: "",
  },
  {
    key: "rejection",
    label: "Rejection",
    enabled: false,
    subject: "",
    body: "",
  },
  {
    key: "offer",
    label: "Offer",
    enabled: false,
    subject: "",
    body: "",
  },
];

const emptyForm = {
  title: "",
  slug: "",
  location: "",
  jobStatus: "Open",
  jobType: "Full-time",
  jobCategoryId: "",
  totalExperience: "",
  employeeType: "Permanent",
  numberOfResources: "",
  hiringForDepartment: "",
  jobTagIds: [],
  minSalary: "",
  maxSalary: "",
  description: "",
  featuredImage: "",
  pipeline: defaultPipeline,
  applicationQuestions: defaultApplicationQuestions,
  automatedEmails: defaultEmails,
  // New: assignment
  assignedTo: null,
  meta: {},
};

const slugify = (text = "") =>
  text
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const JobPostingForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [users, setUsers] = useState([]);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Permissions
  const loggedUser = useSelector((state) => state.user.user || {});
  const userPermissions = loggedUser.userPermission || [];
  const canCreateJob = userPermissions.includes("create Job posting");
  const canUpdateJob = userPermissions.includes("update Job posting");
  const isAllowed = (!isEdit && canCreateJob) || (isEdit && canUpdateJob);

  const updateField = (key, value) =>
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

  const toggleApplicationQuestion = (key) => {
    setForm((prev) => ({
      ...prev,
      applicationQuestions: {
        ...prev.applicationQuestions,
        [key]: {
          enabled: !prev.applicationQuestions[key]?.enabled,
        },
      },
    }));
  };

  const updateAutomatedEmail = (index, value) =>
    setForm((prev) => {
      const next = [...prev.automatedEmails];
      next[index] = { ...next[index], ...value };
      return { ...prev, automatedEmails: next };
    });

  const addPipelineStage = () => {
    const name = prompt("Stage name?");
    if (!name) return;
    updateField("pipeline", [
      ...form.pipeline,
      { name, color: "#0ea5e9" },
    ]);
  };

  const fetchTaxonomies = async () => {
    try {
      const [catRes, tagRes, userRes] = await Promise.all([
        axios.get(jobCategoriesApi, { withCredentials: true }),
        axios.get(jobTagsApi, { withCredentials: true }),
        axios.get(jobPostingsUserList, { withCredentials: true }),
      ]);
      setCategories(catRes?.data?.data || []);
      setTags(tagRes?.data?.data || []);
      setUsers(userRes?.data?.data || []);
    } catch (error) {
      console.error("job taxonomies / users fetch error", error);
      toast.error("Unable to load categories/tags/users");
    }
  };

  useEffect(() => {
    fetchTaxonomies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTag = (tagId) => {
    setForm((prev) => {
      const exists = prev.jobTagIds.includes(tagId);
      return {
        ...prev,
        jobTagIds: exists
          ? prev.jobTagIds.filter((idItem) => idItem !== tagId)
          : [...prev.jobTagIds, tagId],
      };
    });
  };

  const removePipelineStage = (name) => {
    updateField(
      "pipeline",
      form.pipeline.filter((item) => item.name !== name)
    );
  };

  const movePipelineStage = (index, direction) => {
    const next = [...form.pipeline];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    updateField("pipeline", next);
  };

  const fetchPosting = async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${jobPostingsApi}/${id}`, {
        withCredentials: true,
      });
      const record = data?.data || {};
      setForm({
        ...emptyForm,
        ...record,
        jobCategoryId:
          record.jobCategoryId ??
          record.jobCategory ??
          "",
        jobTagIds:
          Array.isArray(record.jobTagIds) && record.jobTagIds.length
            ? record.jobTagIds
            : Array.isArray(record.jobTags) && record.jobTags.length
            ? record.jobTags
            : typeof record.jobTags === "string" && record.jobTags.length
            ? record.jobTags.split(",").map((t) => t.trim())
            : [],
        pipeline: record.pipeline?.length ? record.pipeline : defaultPipeline,
        applicationQuestions: (() => {
          const existing = record.applicationQuestions || {};
          // Convert old boolean format to new { enabled: boolean } format if needed
          const normalized = {};
          Object.keys(defaultApplicationQuestions).forEach((key) => {
            if (existing[key] !== undefined) {
              normalized[key] =
                typeof existing[key] === "boolean"
                  ? { enabled: existing[key] }
                  : existing[key]?.enabled !== undefined
                  ? existing[key]
                  : { enabled: false };
            } else {
              normalized[key] = defaultApplicationQuestions[key];
            }
          });
          return normalized;
        })(),
        automatedEmails:
          record.automatedEmails?.length === 0
            ? defaultEmails
            : record.automatedEmails || defaultEmails,
        // Ensure assignedTo is a simple id or null
        assignedTo:
          record.assignedTo ??
          record.assignedUser?.id ??
          null,
        numberOfResources:
          record.numberOfResources !== undefined &&
          record.numberOfResources !== null
            ? String(record.numberOfResources)
            : "",
        hiringForDepartment:
          record.hiringForDepartment !== undefined &&
          record.hiringForDepartment !== null
            ? String(record.hiringForDepartment)
            : "",
        meta: record.meta || {},
      });
    } catch (error) {
      console.error("jobPosting.fetch ui error", error);
      toast.error("Unable to load job posting");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!form.title) return;
    const slug = slugify(form.title);
    if (form.slug !== slug) {
      updateField("slug", slug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title]);

  const tagsById = useMemo(() => {
    const map = {};
    tags.forEach((t) => {
      map[String(t.id)] = t.name || t.title || t.label;
    });
    return map;
  }, [tags]);

  const tagOptions = useMemo(
    () =>
      tags.map((t) => ({
        value: t.id,
        label: t.name || t.title || t.label,
      })),
    [tags]
  );

  const validateStep = (currentStep) => {
    const nextErrors = {};

    if (currentStep === 0) {
      if (!form.title || !form.title.trim()) {
        nextErrors.title = "Job title is required";
      }

      const min = form.minSalary !== "" ? Number(form.minSalary) : null;
      const max = form.maxSalary !== "" ? Number(form.maxSalary) : null;
      const resources =
        form.numberOfResources !== "" ? Number(form.numberOfResources) : null;

      if (min !== null && (Number.isNaN(min) || min < 0)) {
        nextErrors.minSalary = "Min salary must be a positive value";
      }
      if (max !== null && (Number.isNaN(max) || max < 0)) {
        nextErrors.maxSalary = "Max salary must be a positive value";
      }
      if (resources !== null) {
        if (Number.isNaN(resources) || resources < 0) {
          nextErrors.numberOfResources =
            "Number of resources must be a non-negative number";
        }
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!isAllowed) {
      toast.error("You do not have permission to perform this action");
      return;
    }
    if (!validateStep(0)) {
      toast.error("Please fix the errors in the Job information step");
      setStep(0);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        jobCategoryId: form.jobCategoryId || null,
        jobTagIds: Array.isArray(form.jobTagIds)
          ? form.jobTagIds.filter(Boolean)
          : [],
        minSalary: form.minSalary || null,
        maxSalary: form.maxSalary || null,
        assignedTo: form.assignedTo || null,
        numberOfResources:
          form.numberOfResources === ""
            ? null
            : Number(form.numberOfResources),
        hiringForDepartment:
          form.hiringForDepartment === ""
            ? null
            : Number(form.hiringForDepartment),
      };
      if (!payload.slug) payload.slug = slugify(payload.title);
      const request = isEdit
        ? axios.put(`${jobPostingsApi}/${id}`, payload, {
            withCredentials: true,
          })
        : axios.post(jobPostingsApi, payload, { withCredentials: true });

      const { data } = await request;
      toast.success(isEdit ? "Job posting updated" : "Job posting created");
      const recordId = data?.data?.id || id;
      navigate(`/admin/job-postings/${recordId || ""}/preview`);
    } catch (error) {
      console.error("jobPosting.save ui error", error);
      toast.error("Unable to save job posting");
    } finally {
      setLoading(false);
    }
  };

  const StepActions = () => (
    <div className="flex items-center justify-between border-t border-gray-200 pt-4">
      <button
        type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        disabled={step === 0}
        className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
      >
        Back
      </button>
      {step < steps.length - 1 ? (
        <button
          type="button"
          onClick={() => {
            if (validateStep(step)) {
              setStep((s) => Math.min(steps.length - 1, s + 1));
            }
          }}
          className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:shadow-md"
        >
          Next step
        </button>
      ) : (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:shadow-md disabled:opacity-60"
        >
          {loading ? "Saving..." : isEdit ? "Update job" : "Create job"}
        </button>
      )}
    </div>
  );

  if (!isAllowed) {
    return (
      <div className="rounded-xl bg-white p-6 text-center text-sm text-red-600 shadow">
        You are not authorized to {isEdit ? "update" : "create"} job postings.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between rounded-2xl bg-indigo-600 p-4 text-white shadow">
        <div>
          <p className="text-xs uppercase tracking-wide opacity-80">
            {isEdit ? "Update job posting" : "Create job posting"}
          </p>
          <h1 className="text-xl font-semibold">Multi-step form</h1>
        </div>
        <div className="text-sm font-semibold">
          Step {step + 1} of {steps.length}
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-sm">
        <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-4">
          <div className="space-y-4 lg:col-span-1">
            {steps.map((label, index) => (
              <button
                key={label}
                onClick={() => setStep(index)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-semibold ${
                  index === step
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border text-xs">
                    {index + 1}
                  </span>
                  {label}
                </span>
                <span className="text-xs">
                  {index < step ? "Done" : index === step ? "Now" : ""}
                </span>
              </button>
            ))}
          </div>

          <div className="space-y-6 lg:col-span-3">
            {step === 0 && (
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Job title*
                    </label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => updateField("title", e.target.value)}
                      className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                        errors.title
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-200 focus:border-primaryBlue focus:ring-primaryBlue"
                      }`}
                    />
                    {errors.title && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.title}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Slug
                    </label>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => updateField("slug", e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Location
                    </label>
                    <input
                      type="text"
                      value={form.location}
                      onChange={(e) => updateField("location", e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    />
                  </div>
                  <div>
                    <FileUpload
                      endpoint={jobPostingFeaturedImageUploadApi}
                      fieldName="featuredImage"
                      onUploadComplete={(url) =>
                        updateField("featuredImage", url)
                      }
                      accept="image/*"
                      label="Featured image"
                      currentUrl={form.featuredImage}
                    />
                  </div>
                </div>

                {/* Job category / experience / assignment */}
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Job status
                    </label>
                    <select
                      value={form.jobStatus}
                      onChange={(e) => updateField("jobStatus", e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    >
                      <option>Open</option>
                      <option>Interviewing</option>
                      <option>Offer in Progress</option>
                      <option>Position Filled</option>
                      <option>Closed</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Job type
                    </label>
                    <select
                      value={form.jobType}
                      onChange={(e) => updateField("jobType", e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    >
                      <option>Full-time</option>
                      <option>Part-time</option>
                      <option>Contract</option>
                      <option>Internship</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Employee type
                    </label>
                    <select
                      value={form.employeeType}
                      onChange={(e) =>
                        updateField("employeeType", e.target.value)
                      }
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    >
                      <option>Permanent</option>
                      <option>Temporary</option>
                    </select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Job category
                    </label>
                    <select
                      value={form.jobCategoryId}
                      onChange={(e) =>
                        updateField("jobCategoryId", e.target.value)
                      }
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name || cat.title || cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Total experience
                    </label>
                    <input
                      type="text"
                      value={form.totalExperience}
                      onChange={(e) =>
                        updateField("totalExperience", e.target.value)
                      }
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                      placeholder="e.g. 5+ years"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Assign to
                    </label>
                    <select
                      value={form.assignedTo || ""}
                      onChange={(e) =>
                        updateField(
                          "assignedTo",
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    >
                      <option value="">
                        Unassigned (visible only to users with view all permissions)
                      </option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name || u.fullName || u.email || `User #${u.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* New fields: Number of resources & Hiring for department */}
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Number of resources
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={form.numberOfResources}
                      onChange={(e) =>
                        updateField("numberOfResources", e.target.value)
                      }
                      className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                        errors.numberOfResources
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-200 focus:border-primaryBlue focus:ring-primaryBlue"
                      }`}
                      placeholder="e.g. 3"
                    />
                    {errors.numberOfResources && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.numberOfResources}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Hiring for department
                    </label>
                    <select
                      value={form.hiringForDepartment || ""}
                      onChange={(e) =>
                        updateField("hiringForDepartment", e.target.value)
                      }
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    >
                      <option value="">Select department</option>
                      <option value="1">VL</option>
                      <option value="2">Vnaya</option>
                      <option value="3">Creed Staffing</option>
                      <option value="4">VLS</option>
                      <option value="5">Aone FF</option>
                      <option value="6">Dispatch</option>
                    </select>
                  </div>
                </div>

                {/* Job tags in a full-width row for better layout */}
                <div className="grid gap-4 md:grid-cols-1">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Job tags
                    </label>
                    <div className="mt-2">
                      <Select
                        isMulti
                        className="react-select-container text-sm"
                        classNamePrefix="react-select"
                        value={tagOptions.filter((opt) =>
                          form.jobTagIds.some(
                            (id) => String(id) === String(opt.value)
                          )
                        )}
                        options={tagOptions}
                        onChange={(selected) =>
                          updateField(
                            "jobTagIds",
                            (selected || []).map((opt) => opt.value)
                          )
                        }
                        placeholder="Select tags"
                        closeMenuOnSelect={false}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Min salary
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={form.minSalary}
                      onChange={(e) => updateField("minSalary", e.target.value)}
                      className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                        errors.minSalary
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-200 focus:border-primaryBlue focus:ring-primaryBlue"
                      }`}
                      placeholder="e.g. 60000"
                    />
                    {errors.minSalary && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.minSalary}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700">
                      Max salary
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={form.maxSalary}
                      onChange={(e) => updateField("maxSalary", e.target.value)}
                      className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                        errors.maxSalary
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-200 focus:border-primaryBlue focus:ring-primaryBlue"
                      }`}
                      placeholder="e.g. 90000"
                    />
                    {errors.maxSalary && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.maxSalary}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700">
                    Description
                  </label>
                  <textarea
                    rows={5}
                    value={form.description}
                    onChange={(e) => updateField("description", e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                  />
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Application questions
                </h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  {Object.keys(defaultApplicationQuestions).map((key) => {
                    const isFileUpload = key === "resume" || key === "coverLetter";
                    return (
                      <label
                        key={key}
                        className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-2">
                          <span className="capitalize">
                            {key.replace(/([A-Z])/g, " $1")}
                          </span>
                          {isFileUpload && (
                            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-700">
                              File Upload
                            </span>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={!!form.applicationQuestions[key]?.enabled}
                          onChange={() => toggleApplicationQuestion(key)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-primaryBlue"
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Job pipeline
                  </h3>
                  <button
                    type="button"
                    onClick={addPipelineStage}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Add stage
                  </button>
                </div>
                <div className="space-y-2">
                  {form.pipeline.map((stage, index) => (
                    <div
                      key={`${stage.name}-${index}`}
                      className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{ background: stage.color }}
                        />
                        <input
                          type="text"
                          value={stage.name}
                          onChange={(e) => {
                            const next = [...form.pipeline];
                            next[index] = { ...stage, name: e.target.value };
                            updateField("pipeline", next);
                          }}
                          className="w-48 rounded border border-gray-200 px-2 py-1 text-sm focus:border-primaryBlue focus:outline-none"
                        />
                        <input
                          type="color"
                          value={stage.color}
                          onChange={(e) => {
                            const next = [...form.pipeline];
                            next[index] = { ...stage, color: e.target.value };
                            updateField("pipeline", next);
                          }}
                          className="h-8 w-12 rounded border border-gray-200"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => movePipelineStage(index, -1)}
                          className="rounded border border-gray-200 px-2 py-1 text-xs text-[#444] hover:bg-gray-50"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => movePipelineStage(index, 1)}
                          className="rounded border border-gray-200 px-2 py-1 text-xs text-[#444] hover:bg-gray-50"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => removePipelineStage(stage.name)}
                          className="rounded border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Automated emails
                </h3>
                <div className="space-y-3">
                  {form.automatedEmails.map((item, index) => (
                    <div
                      key={item.key || index}
                      className="rounded-lg border border-gray-200 p-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-gray-800">
                          {item.label}
                        </div>
                        <label className="flex items-center gap-2 text-xs font-semibold text-[#444]">
                          Enable
                          <input
                            type="checkbox"
                            checked={!!item.enabled}
                            onChange={(e) =>
                              updateAutomatedEmail(index, {
                                enabled: e.target.checked,
                              })
                            }
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-primaryBlue"
                          />
                        </label>
                      </div>
                      <div className="mt-3 space-y-2">
                        <input
                          type="text"
                          value={item.subject || ""}
                          placeholder="Subject"
                          onChange={(e) =>
                            updateAutomatedEmail(index, {
                              subject: e.target.value,
                            })
                          }
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                        />
                        <textarea
                          rows={3}
                          value={item.body || ""}
                          placeholder="Email body"
                          onChange={(e) =>
                            updateAutomatedEmail(index, { body: e.target.value })
                          }
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Review
                </h3>
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <p className="text-xs uppercase text-[#444]">Title</p>
                      <p className="text-sm font-semibold text-gray-800">
                        {form.title || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-[#444]">Location</p>
                      <p className="text-sm font-semibold text-gray-800">
                        {form.location || "-"}
                      </p>
                    </div>
                    {form.featuredImage && (
                      <div>
                        <p className="text-xs uppercase text-[#444]">
                          Featured Image
                        </p>
                        <div className="mt-2">
                          <img
                            src={form.featuredImage}
                            alt="Featured"
                            className="h-20 w-20 rounded-lg object-cover border border-gray-200"
                          />
                        </div>
                      </div>
                    )}
                    <div>
                      <p className="text-xs uppercase text-[#444]">
                        Status / Type
                      </p>
                      <p className="text-sm font-semibold text-gray-800">
                        {form.jobStatus} • {form.jobType}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-[#444]">
                        Experience / Employment
                      </p>
                      <p className="text-sm font-semibold text-gray-800">
                        {form.totalExperience || "-"} • {form.employeeType}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-[#444]">
                        Category
                      </p>
                      <p className="text-sm font-semibold text-gray-800">
                        {categories.find((c) => c.id == form.jobCategoryId)
                          ? categories.find((c) => c.id == form.jobCategoryId)
                              ?.name ||
                            categories.find((c) => c.id == form.jobCategoryId)
                              ?.title
                          : "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-[#444]">
                        Assigned to
                      </p>
                      <p className="text-sm font-semibold text-gray-800">
                        {(() => {
                          if (!form.assignedTo) {
                            return "Unassigned";
                          }
                          const assignedUser =
                            users.find((u) => Number(u.id) === Number(form.assignedTo));
                          return (
                            assignedUser?.name ||
                            assignedUser?.fullName ||
                            assignedUser?.email ||
                            `User #${form.assignedTo}`
                          );
                        })()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-[#444]">
                        Salary range
                      </p>
                      <p className="text-sm font-semibold text-gray-800">
                        {form.minSalary || "-"} - {form.maxSalary || "-"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-xs uppercase text-[#444]">Pipeline</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {form.pipeline.map((item) => (
                        <span
                          key={item.name}
                          className="rounded-full px-3 py-1 text-xs font-semibold text-white"
                          style={{ background: item.color }}
                        >
                          {item.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-xs uppercase text-[#444]">
                      Application questions
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Object.entries(form.applicationQuestions || {})
                        .filter(([, value]) => value?.enabled)
                        .map(([key]) => (
                          <span
                            key={key}
                            className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700"
                          >
                            {key.replace(/([A-Z])/g, " $1")}
                          </span>
                        ))}
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-xs uppercase text-[#444]">
                      Tags
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {form.jobTagIds.length === 0 && (
                        <span className="text-sm text-gray-500">No tags</span>
                      )}
                      {form.jobTagIds.map((tagId) => (
                        <span
                          key={tagId}
                          className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700"
                        >
                          {tagsById[tagId] || tagId}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-xs uppercase text-[#444]">Description</p>
                    <p className="mt-1 text-sm text-gray-700">
                      {form.description || "No description yet."}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <StepActions />
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default JobPostingForm;

